import dayjs from "@/helpers/dayjsConfig.js";

import { pathOptions } from "@/helpers/GlobalVariables.js";
import * as XLSX from "xlsx";

// This Function Returns Unique Array Out Of Provided Array
export let uniqueArrayHandle = (original_array, unique_key) => {
  const uniqueArray = Array.from(
    original_array
      .reduce((map, obj) => map.set(obj[unique_key], obj), new Map())
      .values()
  );
  return uniqueArray;
};

// This Method Autofills Today's Date
export let todayAutofill = (format = "") => {
  const account = JSON.parse(localStorage.getItem("account"));
  let todayIs = dayjs()
    .tz(account?.timezone ?? "America/New_York")
    .format(format ? format : "YYYY-MM-DD");
  return dayjs(todayIs).format("YYYY-MM-DD");
};

// This Method Autofills Tomorrow's Date
export let tomorrowAutofill = (format) => {
  const account = JSON.parse(localStorage.getItem("account"));
  let todayIs = dayjs()
    .tz(account?.timezone ?? "America/New_York")
    .format(format ? format : "YYYY-MM-DD");
  return dayjs(todayIs).add(1, "day").format("YYYY-MM-DD");
};

// This function is used for sorting custom alphanumeric rows in <a-table /> records
export function customAlphanumericSorter(a, b) {
  if (!a || a === "-") return -1;
  if (!b || b === "-") return 1;
  const regex = /([0-9]+)|([^0-9]+)/g;
  const partsA = String(a).match(regex);
  const partsB = String(b).match(regex);

  for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
    const partA = partsA[i];
    const partB = partsB[i];

    if (isNaN(partA) && isNaN(partB)) {
      // Both parts are non-numeric, compare them as strings
      if (partA < partB) return -1;
      if (partA > partB) return 1;
    } else {
      // At least one part is numeric, compare them as numbers
      const numA = parseFloat(partA) || 0;
      const numB = parseFloat(partB) || 0;
      if (numA < numB) return -1;
      if (numA > numB) return 1;
    }
  }

  // If all parts are equal, compare the whole strings
  return String(a).localeCompare(String(b));
}

/**
 * Custom MAC address validation Function.
 * @param {string} macAddress - Mac address.
 * @returns {boolean} returns valid or not
 */
export function validateMacAddress(macAddress, condition = false) {
  // Remove any colons, hyphens, or other special characters
  const tempMac = macAddress.replaceAll(":", "").replaceAll("-", "");
  // check for alphanumeric value
  if (condition == false) {
    if (/^[a-zA-Z0-9]+$/.test(tempMac)) {
      // check for length of MAC address
      if (tempMac.length !== 12 && tempMac.length !== 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }
  // Following message can be included to display custom validation error message
  // validateMacAddress: helpers.withMessage("Invalid MAC address",validateMacAddress)
}

// This Method Will Re-Route User To A List-Page
export let accountChange = (currPath) => {
  let possibleRoutes = [];

  possibleRoutes = pathOptions.filter((item) => {
    if (currPath.includes(item.value)) {
      if (item.hasFilter) {
        item.path = `${item.path}?page=1&size=100`;
        return item;
      } else {
        return item;
      }
    }
  });
  return possibleRoutes;
};

// This Method Will Re-Route User To A List-Page
export let statusCheck = (list) => {
  let possibleStatuses = {
    proposal: "Pending",
    invoice: "Pending",
    shipping: "Pending",
    installation: "Pending",
    subscription: "Pending",
  };

  possibleStatuses.proposal = list.includes("proposal_accepted")
    ? "Accepted"
    : list.includes("proposal_viewed")
    ? "Viewed"
    : "Pending";

  possibleStatuses.invoice = list.includes("invoice_paid")
    ? "Paid"
    : list.includes("invoice_created")
    ? "Created"
    : "Pending";

  possibleStatuses.shipping = list.includes("shipping_delivered")
    ? "Delivered"
    : list.includes("shipping_shipped")
    ? "Shipped"
    : list.includes("shipping_created")
    ? "Created"
    : "Pending";

  possibleStatuses.installation = list.includes("installation_installed")
    ? "Installed"
    : list.includes("installation_created")
    ? "Created"
    : "Pending";

  possibleStatuses.subscription = list.includes("subscription_created")
    ? "Active"
    : "Pending";
  return possibleStatuses;
};

/**
 * @exports Data-As-EXCEL
 * @param {Required} excelData Selected data or pass empty array ([] <= Array)
 * @param {Required} original Whole list of data  ([] <= Array (Mostly table.tableData))
 * @param {Required} accountName this.account.name
 * @param {Required} type type of report (customer, invoice etc.)
 * @param {Required} removeList list for key-value removal ([] <= Array)
 */
export let exportToExcel = (
  excelData,
  original,
  accountName,
  type,
  removeList
) => {
  let filteredObjects = [];
  if (excelData.length !== 0) {
    filteredObjects = original.filter((obj) => excelData.includes(obj.id));
  } else {
    filteredObjects = original;
  }

  // List of keys to remove
  const keysToRemove = [...removeList];

  // Function to remove specified keys from an object
  const removeKeys = (obj, keys) => {
    const newObj = { ...obj };
    keys.forEach((key) => delete newObj[key]);
    return newObj;
  };

  // Map over the array and remove unwanted keys
  const cleanedData = filteredObjects.map((item) =>
    removeKeys(item, keysToRemove)
  );

  // Function to replace underscores with spaces in keys
  const replaceUnderscoresInKeys = (obj) => {
    const newObj = {};
    for (const key in obj) {
      const newKey = key.replaceAll("_", " ");
      newObj[newKey] = obj[key];
    }
    return newObj;
  };

  // Transform Data As Key Value (S7tring)
  const transformedData = cleanedData.map((item) => {
    const cleanedItem = { ...item };
    return replaceUnderscoresInKeys(cleanedItem);
  });

  const ws = XLSX.utils.json_to_sheet(transformedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${accountName} - ${type} List.xlsx`);
};

// To check the company name has exact format #isValidCompanyFormat
export let isValCom = (text) => {
  const regex =
    /^(?!.* {2})(?=.{3,})([A-Z][a-zA-Z0-9]*(?: [A-Za-z0-9&][a-zA-Z0-9]*)*) - ([A-Z][a-z]+(?: [A-Z][a-z]*)*),\s([A-Z]{2})$/;
  return regex.test(text);
};

// To check the value has no leading or trailing spaces #hasNoLeadingOrTrailingSpaces
export let noLOrTSpace = (text) => {
  const regex = /^[^\s].*[^\s]$/;
  return regex.test(text);
};

// To check the value that has no signs
export let noSigns = (text) => {
  var regex = /^[^~`!@#$%^&*()_+={[}\]|\\:;"'<,>.?/]*$/;
  if (!regex.test(text)) {
    return false;
  }
  return true;
};
